import { useCallback, useEffect, useState } from 'react';
import useDigitInput from 'react-digit-input';
import { useDispatch } from 'react-redux';
import { errorToast, maskEmail, successToast } from '../../utils';
import {
	confirmUserEmailChange,
	resendUserEmailChange,
} from '../../store/auth/authActions';

const RESEND_COOLDOWN_SEC = 60;

export const EmailChangeVerifyDialog = ({
	open,
	targetEmail,
	onCancel,
	onSuccess,
}) => {
	const dispatch = useDispatch();
	const [value, onChange] = useState('');
	const [submitting, setSubmitting] = useState(false);
	const [resendLoading, setResendLoading] = useState(false);
	const [resendCooldown, setResendCooldown] = useState(0);

	const digits = useDigitInput({
		acceptedCharacters: /^[0-9]$/,
		length: 6,
		value,
		onChange,
	});

	useEffect(() => {
		if (!open) {
			onChange('');
			setResendCooldown(0);
			return;
		}
		onChange('');
		setResendCooldown(0);
	}, [open, onChange]);

	useEffect(() => {
		if (resendCooldown <= 0) return undefined;
		const id = setTimeout(() => setResendCooldown((c) => Math.max(0, c - 1)), 1000);
		return () => clearTimeout(id);
	}, [resendCooldown]);

	useEffect(() => {
		if (!open) return undefined;
		const onKey = (e) => {
			if (e.key === 'Escape') onCancel();
		};
		window.addEventListener('keydown', onKey);
		return () => window.removeEventListener('keydown', onKey);
	}, [open, onCancel]);

	const handleConfirm = useCallback(
		async (e) => {
			e.preventDefault();
			if (value.trim().length !== 6 || submitting) return;
			setSubmitting(true);
			try {
				await dispatch(confirmUserEmailChange({ code: value.trim() })).unwrap();
				await onSuccess();
				onChange('');
			} catch (err) {
				errorToast(err?.error || err?.message || 'Something went wrong');
			} finally {
				setSubmitting(false);
			}
		},
		[dispatch, onSuccess, submitting, value, onChange],
	);

	const handleResend = async (e) => {
		e.preventDefault();
		if (resendLoading || resendCooldown > 0) return;
		setResendLoading(true);
		try {
			await dispatch(resendUserEmailChange()).unwrap();
			setResendCooldown(RESEND_COOLDOWN_SEC);
			successToast('A new code was sent to your email');
		} catch (err) {
			errorToast(err?.error || err?.message || 'Could not resend code');
		} finally {
			setResendLoading(false);
		}
	};

	if (!open) return null;

	const masked = maskEmail(targetEmail);

	return (
		<div
			className="fixed inset-0 z-[100000] flex items-center justify-center p-4"
			role="dialog"
			aria-modal="true"
			aria-labelledby="email-verify-title"
		>
			<button
				type="button"
				className="absolute inset-0 bg-black/50 backdrop-blur-[2px] cursor-default"
				aria-label="Close dialog"
				onClick={onCancel}
			/>
			<div className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl ring-1 ring-black/5">
				<h2 id="email-verify-title" className="text-xl font-semibold text-nt-blue text-center">
					Confirm your new email
				</h2>
				<p className="mt-2 text-center text-sm text-gray-600 leading-relaxed">
					We sent a 6-digit code to <span className="font-medium text-gray-800">{masked}</span>.
					Enter it below to finish updating your email.
				</p>

				<form onSubmit={handleConfirm} className="mt-6">
					<label className="block text-center text-xs font-medium uppercase tracking-wide text-gray-500 mb-3">
						Verification code
					</label>
					<div className="flex justify-center gap-2 sm:gap-3">
						{[0, 1, 2, 3, 4, 5].map((i) => (
							<input
								key={i}
								{...digits[i]}
								className="h-12 w-10 sm:w-11 rounded-lg border-2 border-nt-grey-light border-b-nt-blue text-center text-xl font-semibold outline-none focus:border-nt-blue focus:ring-2 focus:ring-nt-blue/20 transition-shadow"
								inputMode="numeric"
								autoComplete="one-time-code"
								autoFocus={i === 0}
							/>
						))}
					</div>

					<button
						type="submit"
						disabled={value.trim().length !== 6 || submitting}
						className="mt-6 w-full rounded-lg bg-nt-blue py-3 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-95 transition-opacity"
					>
						{submitting ? 'Verifying...' : 'Verify and save'}
					</button>
				</form>

				<div className="mt-4 flex flex-col items-center gap-2">
					<button
						type="button"
						disabled={resendLoading || resendCooldown > 0}
						onClick={handleResend}
						className="text-sm text-nt-blue font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:underline"
					>
						{resendLoading
							? 'Sending...'
							: resendCooldown > 0
								? `Resend code in ${resendCooldown}s`
								: 'Resend code'}
					</button>
					<button
						type="button"
						onClick={onCancel}
						className="text-sm text-gray-500 hover:text-gray-800"
					>
						Cancel
					</button>
				</div>
			</div>
		</div>
	);
};
